import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Button, Spin } from "antd";
import { returnUpDownClassname } from "utils/helpers";
import { errorText, money, percent, tradeLabel } from "./format";

// Closed real trades, 30 days at a time (GET /api/miniapp/history), newest
// first; "Load more" asks for the 30 days before the oldest loaded one. P&L is
// net of commissions.
const HistoryTab = ({ api }) => {
  const [trades, setTrades] = useState(null);
  const [from, setFrom] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (to) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/api/miniapp/history", {
          params: to ? { to } : {},
        });
        setTrades((current) => (to ? [...(current || []), ...data.trades] : data.trades));
        setFrom(data.from);
        setHasOlder(data.hasOlder);
      } catch (err) {
        setError(errorText(err, "Couldn't load the history"));
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const list = trades || [];
    const value = list.reduce((sum, t) => sum + (t.netProfit?.value || 0), 0);
    const wins = list.filter((t) => t.netProfit?.value > 0).length;
    return {
      value,
      count: list.length,
      winRate: list.length ? (wins / list.length) * 100 : null,
    };
  }, [trades]);

  if (!trades) {
    return error ? (
      <p className="mini-app-empty">{error}</p>
    ) : (
      <div className="mini-app-center">
        <Spin />
      </div>
    );
  }

  return (
    <>
      <div className="mini-app-header">
        <div>
          <div className="mini-app-title">Trades history</div>
          <div className="mini-app-sub">
            Since {dayjs(from).format("DD/MM/YYYY")} · {summary.count} trades
            {summary.winRate !== null && ` · ${summary.winRate.toFixed(0)}% wins`}
          </div>
        </div>
        <div className={`mini-app-total ${returnUpDownClassname(summary.value)}`}>
          <div>{money(summary.value)}</div>
          <div className="mini-app-total-perc">net</div>
        </div>
      </div>

      {trades.length === 0 && (
        <p className="mini-app-empty">No closed trades in the last 30 days.</p>
      )}

      {trades.map((trade) => (
        <div key={trade.tradeId} className="mini-app-card">
          <div className="mini-app-row">
            <div>
              <div className="mini-app-symbol">{tradeLabel(trade)}</div>
              <div className="mini-app-sub">{trade.strategyName}</div>
            </div>
            <div
              className={`mini-app-pnl ${returnUpDownClassname(trade.netProfit?.value)}`}
            >
              <div className="mini-app-pnl-value">{money(trade.netProfit?.value)}</div>
              <div className="mini-app-pnl-perc">{percent(trade.netProfit?.perc)}</div>
            </div>
          </div>
          <div className="mini-app-details">
            <span>
              {trade.openRate} <small>open</small>
            </span>
            <span>
              {trade.closeRate} <small>close</small>
            </span>
            <span>
              {trade.amount} <small>{trade.option ? "contracts" : "shares"}</small>
            </span>
          </div>
          <div className="mini-app-row mini-app-footer">
            <span className="mini-app-sub mini-app-reason">
              {trade.closeReason || "-"}
            </span>
            <span className="mini-app-sub">
              {dayjs(trade.closeDate).format("DD/MM HH:mm")}
            </span>
          </div>
        </div>
      ))}

      {error && <p className="mini-app-empty">{error}</p>}
      {hasOlder && (
        <Button block loading={loading} onClick={() => load(from)}>
          Load older trades
        </Button>
      )}
    </>
  );
};

export default HistoryTab;
